import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, TrendingUp, RefreshCw } from 'lucide-react';

/**
 * Компонент для отображения графика изменения курса валют
 * 
 * @param {Object} historicalData - Исторические данные курса
 * @param {Object} currencies - Объект с информацией о валютах
 * @param {boolean} loading - Флаг загрузки
 */
const CurrencyChart = ({ historicalData, currencies, loading }) => {
  // Периоды отображения данных
  const periods = [
    { id: '7d', name: '7 дней' },
    { id: '14d', name: '14 дней' },
    { id: '30d', name: '30 дней' }
  ];
  
  // Состояние для выбранного периода
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  
  // Если нет данных или они загружаются, показываем загрузчик
  if (!historicalData || loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <TrendingUp className="mr-2 h-6 w-6 text-indigo-500" />
          Динамика курса
        </h2>
        
        <div className="flex justify-center items-center h-60">
          <RefreshCw className="h-8 w-8 text-indigo-500 animate-spin" />
        </div>
      </div>
    );
  }
  
  // Получаем данные для выбранного периода
  const getPeriodData = () => {
    const { data } = historicalData;
    
    if (!data || data.length === 0) return [];
    
    let days = 7;
    if (selectedPeriod === '14d') days = 14;
    if (selectedPeriod === '30d') days = 30;
    
    return data.slice(-days);
  };
  
  // Данные для выбранного периода
  const chartData = getPeriodData();
  
  // Функция форматирования даты для оси X
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };
  
  // Функция форматирования значения для тултипа
  const formatTooltipValue = (value) => {
    return value.toFixed(4);
  };
  
  // Функция форматирования значения для тултипа даты
  const formatTooltipDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  // Пользовательский компонент тултипа
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-md text-sm">
          <p className="font-medium text-gray-900 dark:text-white mb-1">{formatTooltipDate(label)}</p>
          <p className="text-indigo-600 dark:text-indigo-400">
            1 {historicalData.fromCurrency} = {formatTooltipValue(payload[0].value)} {historicalData.toCurrency}
          </p>
        </div>
      );
    }
  
    return null;
  };
  
  // Расчет минимального и максимального значения для оси Y
  const minRate = Math.min(...chartData.map(d => d.rate)) * 0.995;
  const maxRate = Math.max(...chartData.map(d => d.rate)) * 1.005;
  
  // Расчет процентного изменения за период
  const calculateChange = () => {
    if (chartData.length < 2) return { value: 0, isPositive: true };
    
    const firstRate = chartData[0].rate;
    const lastRate = chartData[chartData.length - 1].rate;
    const change = ((lastRate - firstRate) / firstRate) * 100;
    
    return {
      value: Math.abs(change).toFixed(2),
      isPositive: change >= 0
    };
  };
  
  const change = calculateChange();
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <TrendingUp className="mr-2 h-6 w-6 text-indigo-500" />
        Динамика курса {historicalData.fromCurrency} к {historicalData.toCurrency}
      </h2>
      
      {/* Переключатель периодов */}
      <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-4">
        {periods.map(period => (
          <button
            key={period.id}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              selectedPeriod === period.id
                ? 'bg-white dark:bg-gray-600 shadow-sm text-indigo-600 dark:text-indigo-400'
                : 'text-gray-600 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-600/50'
            }`}
            onClick={() => setSelectedPeriod(period.id)}
          >
            {period.name}
          </button>
        ))}
      </div>
      
      {/* Информация об изменении курса */}
      <div className="flex flex-wrap items-center mb-4">
        <div className="flex items-center mr-6 mb-2">
          <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Период: {formatDate(chartData[0]?.date || '')} - {formatDate(chartData[chartData.length - 1]?.date || '')}
          </span>
        </div>
        
        <div className="flex items-center mb-2">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            change.isPositive 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
          }`}>
            {change.isPositive ? (
              <TrendingUp className="h-3.5 w-3.5 mr-1" />
            ) : (
              <TrendingUp className="h-3.5 w-3.5 mr-1 transform rotate-180" />
            )}
            {change.isPositive ? '+' : '-'}{change.value}%
          </span>
        </div>
      </div>
      
      {/* График */}
      <div className="h-60 md:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate} 
              stroke="#9ca3af"
              tick={{ fill: '#6b7280' }}
            />
            <YAxis 
              domain={[minRate, maxRate]} 
              tickFormatter={(value) => value.toFixed(4)}
              stroke="#9ca3af"
              tick={{ fill: '#6b7280' }}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="rate" 
              stroke="#6366f1" 
              strokeWidth={2}
              activeDot={{ r: 6 }} 
              dot={{ r: 2 }}
              name={`Курс ${historicalData.fromCurrency}/${historicalData.toCurrency}`}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        * График показывает динамику курса валютной пары {historicalData.fromCurrency}/{historicalData.toCurrency}
      </div>
    </div>
  );
};

export default CurrencyChart;